import React, { useEffect, useState, useCallback } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { Landmark, Plus, Trash2 } from 'lucide-react';
import { useConfirm } from '../../components/ConfirmDialog';
import { Register, DetailDrawer, Field, FormRow, fmtDate, money } from './design';

// The national Support at Home figures (lifetime caps, contribution rates, classification budgets,
// pathway amounts) that Rostery keeps for every provider, each with the date it applies from. A
// service is priced with the figure in force on the day it was delivered, so a change is always a
// new row. Providers read them here; only a Rostery super admin can add one.
const fmtValue = (r) => {
  if (r.unit === 'aud') return money(r.value);
  if (r.unit === 'pct') return `${Number(r.value)}%`;
  if (r.unit === 'days') return `${Number(r.value)} days`;
  return String(r.value);
};

// The document's name, with its address as a link rather than a long path in brackets.
const LINK = /\s*\(((?:https?:\/\/)?[a-z0-9.-]+\.gov\.au\/[^\s)]+)\)\s*$/i;
function SourceCell({ source, notes }) {
  const m = String(source || '').match(LINK);
  const name = m ? String(source).slice(0, m.index) : source;
  const href = m ? (/^https?:/i.test(m[1]) ? m[1] : `https://${m[1]}`) : null;
  return (
    <span className="text-[12px] text-slate-500">
      {name}
      {href && <> <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary-700 underline underline-offset-2 whitespace-nowrap">Open</a></>}
      {notes ? <span className="block text-slate-400">{notes}</span> : null}
    </span>
  );
}

export default function GovFigures() {
  const confirm = useConfirm();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ key: '', value: '', effective_from: '', source: '', notes: '' });
  const [busy, setBusy] = useState(false);
  const close = useCallback(() => setAdding(false), []);

  const load = useCallback(async () => {
    setError(null);
    try { setData((await api.get('/aged-care/reference')).data); } catch (e) { setError(e.response?.data?.message || 'Could not load the government figures'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true);
    try {
      await api.post('/aged-care/reference', form);
      toast.success('Figure added. It applies from its start date.');
      setAdding(false); setForm({ key: '', value: '', effective_from: '', source: '', notes: '' }); load();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not save the figure'); } finally { setBusy(false); }
  };
  // A figure is kept for every provider, so withdrawing one asks first and says what takes its place.
  const withdraw = async (r) => {
    const ok = await confirm(
      `${r.label}: ${fmtValue(r)} from ${fmtDate(r.from)} is withdrawn for every provider before it starts. The value in force before it carries on.`,
      { title: 'Withdraw this figure?', confirmLabel: 'Withdraw figure', danger: true },
    );
    if (!ok) return;
    try { await api.delete(`/aged-care/reference/${r.id}`); toast.success('Figure withdrawn'); load(); }
    catch (e) { toast.error(e.response?.data?.message || 'Could not withdraw it'); }
  };

  const rows = (data?.rows || []).filter((r) => showAll || r.current || r.upcoming);
  const columns = [
    { key: 'label', header: 'Figure', render: (r) => <span className="text-slate-900">{r.label}<span className="block text-[11px] text-slate-500">{r.group}</span></span> },
    { key: 'value', header: 'Value', align: 'right', render: (r) => <span className="tabular-nums font-medium">{fmtValue(r)}</span> },
    { key: 'from', header: 'Applies from', render: (r) => (
      <span className="whitespace-nowrap">{fmtDate(r.from)}{r.upcoming && <span className="ml-1.5 rounded-full bg-sky-50 px-1.5 py-0.5 text-[11px] font-medium text-sky-700">Upcoming</span>}{!r.current && !r.upcoming && <span className="ml-1.5 text-[11px] text-slate-400">Earlier</span>}</span>
    ) },
    { key: 'source', header: 'Source', render: (r) => <SourceCell source={r.source} notes={r.notes} /> },
    ...(data?.canEdit ? [{ key: 'act', header: '', align: 'right', render: (r) => (r.upcoming
      ? <button type="button" onClick={() => withdraw(r)} className="btn-secondary btn-sm" aria-label={`Withdraw ${r.label} from ${r.from}`}><Trash2 size={13} /> Withdraw</button> : null) }] : []),
  ];
  const keys = Object.entries(data?.keys || {});
  return (
    <>
      <Register
        title="Government figures" icon={Landmark} className="mb-5"
        count={rows.length} countLabel="figures"
        actions={(
          <>
            <button type="button" className="btn-secondary btn-sm" onClick={() => setShowAll((v) => !v)}>{showAll ? 'Show figures in force' : 'Show earlier figures too'}</button>
            {data?.canEdit && <button type="button" className="btn-primary btn-sm" onClick={() => setAdding(true)}><Plus size={14} /> Add a figure</button>}
          </>
        )}
        columns={columns} rows={rows} rowKey="id" loading={!data && !error} error={error} onRetry={load} dense
        empty={{ icon: Landmark, title: 'No government figures loaded' }}
        footer={`Kept up to date by Rostery from the Department of Health, Disability and Ageing's published schedules. Each service is priced with the figure in force on the day it was delivered. Figures as at ${data?.asAt ? fmtDate(data.asAt) : '—'}. If one looks wrong, tell us.`}
      />
      {adding && (
        <DetailDrawer open onClose={close} icon={Landmark} width="md" title="Add a government figure"
          subtitle="A new value from a start date. Earlier services keep the value that applied to them."
          footer={(
            <>
              <button type="button" onClick={close} className="btn-secondary">Cancel</button>
              <button type="button" onClick={save} disabled={busy} className="btn-primary">{busy ? 'Saving' : 'Add figure'}</button>
            </>
          )}>
          <div className="space-y-3">
            <Field label="Figure" required>
              {(id) => (
                <select id={id} className="input-field" value={form.key} onChange={(e) => setForm((f) => ({ ...f, key: e.target.value }))}>
                  <option value="">Choose…</option>
                  {keys.map(([k, v]) => <option key={k} value={k}>{v.group}: {v.label}</option>)}
                </select>
              )}
            </Field>
            <FormRow cols={2}>
              <Field label="Value" required hint="Dollars, percent or days, as the figure is measured.">
                {(id) => <input id={id} type="number" step="0.01" min="0" className="input-field" value={form.value} onChange={(e) => setForm((f) => ({ ...f, value: e.target.value }))} />}
              </Field>
              <Field label="Applies from" required>
                {(id) => <input id={id} type="date" className="input-field" value={form.effective_from} onChange={(e) => setForm((f) => ({ ...f, effective_from: e.target.value }))} />}
              </Field>
            </FormRow>
            <Field label="Source" required hint="The document and page it comes from, so anyone can check it.">
              {(id) => <input id={id} className="input-field" value={form.source} onChange={(e) => setForm((f) => ({ ...f, source: e.target.value }))} />}
            </Field>
            <Field label="Notes">
              {(id) => <input id={id} className="input-field" value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />}
            </Field>
          </div>
        </DetailDrawer>
      )}
    </>
  );
}
